import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';

import { DEFAULT_QUANTITY_UNITS, formatQuantityWithUnit, resolveDisplayUnit } from '../../components/quantityUnit';
import { formatVendorDeviceLabel, readVendorDeviceMeta } from '../../components/resourceConfig';
import { hasVendorDeviceSelection } from '../../components/vendorAndDeviceRadioList';
import { VENDOR_DEVICE_FIELD_KEYS, buildVendorDeviceMetaFormPatch, buildVendorDeviceMetaWatchFields } from '../../components/vendorDevice';
import { copyOptionMetaBetweenForms } from '../../platform/option-meta';
import { hasMeaningfulValue } from '../../utils';
import { isVendorQuantityOptional } from '../../utils/postPaidPricing';
import { IMAGE_OPTION_META_KEYS } from '../image/fieldKeys';
import { DEFAULT_SYSTEM_DISK_SIZE_GI, SYSTEM_DISK_MODAL_PRICING_FIELD, SYSTEM_DISK_MODAL_SIZE_FIELD } from './constants';
import type { SystemDiskSizePaths } from './types';

/** 从系统镜像 metadata 解析最小/推荐系统盘 Gi */
export function parseSystemVolumeSizeGi(item: CommonImageItemType | undefined, fallback = DEFAULT_SYSTEM_DISK_SIZE_GI): number {
  const raw = item?.options?.systemImage?.systemVolumeSize;
  if (raw == null || raw === '') return fallback;
  const num = parseFloat(String(raw).replace(/Gi/i, ''));
  return Number.isNaN(num) ? fallback : num;
}

/**
 * 弹窗默认容量：优先已填值 → 镜像推荐 → fallback（默认 10Gi）。
 * 仅用于打开弹窗的初始展示，不写回主表单。
 */
export function resolveSystemDiskModalDefaultSize(
  parentValues: Record<string, unknown>,
  paths: SystemDiskSizePaths,
  fallback = DEFAULT_SYSTEM_DISK_SIZE_GI,
): number {
  const rawSize = get(parentValues, paths.size);
  const sizeNum = Number(rawSize);
  if (hasMeaningfulValue(rawSize) && Number.isFinite(sizeNum) && sizeNum > 0) {
    return sizeNum;
  }

  const imageKey = String(get(parentValues, 'basic.image.key') ?? '').trim();
  const optionMeta = parentValues.optionMeta as Record<string, unknown> | undefined;
  const meta = (imageKey ? optionMeta?.[imageKey] : undefined) ?? optionMeta?.[IMAGE_OPTION_META_KEYS.image] ?? optionMeta?.image;
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const fromImage = parseSystemVolumeSizeGi(meta as CommonImageItemType, 0);
    if (fromImage > 0) return fromImage;
  }

  return fallback > 0 ? fallback : DEFAULT_SYSTEM_DISK_SIZE_GI;
}

/**
 * 读取主表单完整 store。
 * Arco `getFieldsValue()` 仅返回已注册 FormItem，编辑回填的 systemDiskPricing 可能尚未挂载叶子字段而被漏掉。
 */
export function readSystemDiskFormStore(form: FormInstance): Record<string, unknown> {
  return form.getFields() as Record<string, unknown>;
}

/** 系统盘相关 watch 字段（容量 + 计价对象 + 叶子字段），保证回填后卡片态 / 询价都能刷新 */
export function buildSystemDiskWatchFields(paths: SystemDiskSizePaths): string[] {
  return [paths.size, ...buildVendorDeviceMetaWatchFields(paths.pricing)];
}

/** 主表单是否已有系统盘数据（有容量或厂商设备任一即可，用于展示卡片） */
export function hasSystemDiskFormData(formValues: Record<string, unknown>, paths: SystemDiskSizePaths): boolean {
  const hasVendor = hasVendorDeviceSelection(readVendorDeviceMeta(formValues, paths.pricing));
  return hasMeaningfulValue(get(formValues, paths.size)) || hasVendor;
}

/** 主表单系统盘是否已完整配置（校验用：有大小；计价时还需厂商设备；后付费/自定义规格创建仅需厂商设备） */
export function isSystemDiskConfigured(
  formValues: Record<string, unknown>,
  paths: SystemDiskSizePaths,
  billModelId?: string,
  paymentTiming?: string,
  skuType?: string,
  resourceEditMode?: string,
): boolean {
  const hasVendor = hasVendorDeviceSelection(readVendorDeviceMeta(formValues, paths.pricing));
  if (isVendorQuantityOptional({ billModelId, paymentTiming, skuType, resourceEditMode }) && hasVendor) {
    return true;
  }
  if (!hasMeaningfulValue(get(formValues, paths.size))) return false;
  if (!billModelId) return true;
  return hasVendor;
}

/** 卡片摘要行 */
export function buildSystemDiskSummaryItems(
  form: FormInstance,
  paths: SystemDiskSizePaths,
  pricing?: boolean,
): { label: string; value: string }[] {
  const formValues = readSystemDiskFormStore(form);
  const size = get(formValues, paths.size);
  const pricingMeta = readVendorDeviceMeta(formValues, paths.pricing);
  const unit = resolveDisplayUnit({
    quantityUnit: pricingMeta?.quantityUnit,
    pricing,
    defaultUnit: DEFAULT_QUANTITY_UNITS.disk,
    lockDisplayToDefault: true,
  });
  const sizeText = hasMeaningfulValue(size) ? formatQuantityWithUnit(size, unit) : '-';
  return [
    { label: '资源配置', value: sizeText },
    { label: '厂商设备', value: formatVendorDeviceLabel(pricingMeta) },
  ];
}

/** 打开弹窗时注入独立 Form 的初始值（空态默认原 10Gi / 镜像推荐） */
export function buildSystemDiskModalFormValues(
  parentValues: Record<string, unknown>,
  paths: SystemDiskSizePaths,
  defaultSize: number = DEFAULT_SYSTEM_DISK_SIZE_GI,
): Record<string, unknown> {
  const size = resolveSystemDiskModalDefaultSize(parentValues, paths, defaultSize);
  const pricingMeta = readVendorDeviceMeta(parentValues, paths.pricing);
  return {
    [SYSTEM_DISK_MODAL_SIZE_FIELD]: size,
    ...buildVendorDeviceMetaFormPatch(SYSTEM_DISK_MODAL_PRICING_FIELD, pricingMeta),
  };
}

/** 弹窗确认写回主表单；未拖动滑条时仍写入默认容量 */
export function applySystemDiskModalFormValues(
  parentForm: FormInstance,
  paths: SystemDiskSizePaths,
  modalValues: Record<string, unknown>,
  defaultSize: number = DEFAULT_SYSTEM_DISK_SIZE_GI,
) {
  const rawSize = get(modalValues, SYSTEM_DISK_MODAL_SIZE_FIELD);
  const sizeNum = Number(rawSize);
  const size = hasMeaningfulValue(rawSize) && Number.isFinite(sizeNum) && sizeNum > 0 ? sizeNum : defaultSize;
  const pricingMeta = readVendorDeviceMeta(modalValues, SYSTEM_DISK_MODAL_PRICING_FIELD);
  parentForm.setFieldsValue({
    [paths.size]: size,
    ...buildVendorDeviceMetaFormPatch(paths.pricing, pricingMeta),
  });
  copyOptionMetaBetweenForms(parentForm, modalValues, [SYSTEM_DISK_MODAL_PRICING_FIELD]);
}

/** 删除系统盘配置 */
export function clearSystemDiskFields(form: FormInstance, paths: SystemDiskSizePaths) {
  const clearedPricing = Object.fromEntries(VENDOR_DEVICE_FIELD_KEYS.map((key) => [`${paths.pricing}.${key}`, undefined]));
  form.setFieldsValue({
    [paths.size]: undefined,
    [paths.pricing]: undefined,
    ...clearedPricing,
  });
}
